<template>
  <dialog ref="dialog" class="gam-expanded-image-dialog" @close="hideDialog">
    <transition name="fade">
      <div
        v-if="visible"
        ref="expandImage"
        class="gam-expanded-frame"
        :class="{ mobile: isMobile }"
        @dragover="stopEvent"
        @click.self="closeModal"
      >
        <div :class="['gam-expand-image', format, { 'is-mobile': isPhone }]" @click.self="closeModal">
          <div class="viewer">
            <div v-if="showArrows" class="viewer-arrow">
              <gam-button
                v-if="selectedImageIndex > 0"
                :is-icon-only="true"
                :left-icon="GamIconName.ARROW_LEFT"
                :variant="GamButtonVariant.STEKLO"
                :size="GamButtonSize.ACTIONS"
                @click="selectImage(selectedImageIndex - 1)"
              />
            </div>

            <div class="viewer-column">
              <div v-if="showTop" class="viewer-row viewer-top">
                <span />

                <div v-if="images.length > 1" class="image-selector">
                  <div class="dot-viewport" :class="{ windowed: images.length > MAX_VISIBLE_DOTS }">
                    <div class="dot-track" :style="dotTrackStyle">
                      <span
                        v-for="(image, index) in images"
                        :key="image.id"
                        class="dot-select"
                        :class="{ selected: index === selectedImageIndex }"
                        @click="selectImage(index)"
                      />
                    </div>
                  </div>
                </div>
                <span v-else />

                <div v-if="addShortcut || canChange || getMoreItems.items?.length" class="viewer-end">
                  <button
                    v-if="addShortcut"
                    type="button"
                    class="add-photo"
                    :aria-label="localize('handshake.photo.add')"
                    @click="emits('add')"
                  >
                    <GamIcon :name="GamIconName.IMAGE" size="16px" is-current />
                  </button>
                  <div v-if="canChange" class="change-wrapper">
                    <gam-button
                      :is-icon-only="true"
                      :left-icon="GamIconName.REFRESH"
                      :variant="GamButtonVariant.STEKLO"
                      :size="GamButtonSize.ACTIONS"
                      @click="handleChange"
                    />
                  </div>
                  <div v-else-if="getMoreItems.items?.length" class="more-wrapper">
                    <gam-more v-bind="getMoreItems" />
                  </div>
                </div>
                <span v-else />
              </div>

              <div
                ref="imageWrapperRef"
                class="image-wrapper"
                :class="{ skeleton: isLoading || isChanging, 'no-filter': noFilter }"
              >
                <img :class="{ 'is-loading': isLoading, 'is-loaded': isLoaded }" :src="imageSource" />
              </div>

              <div v-if="showBottom" class="viewer-row viewer-bottom">
                <div class="author-time">
                  <gam-player-card
                    v-if="showAuthor && images[selectedImageIndex]?.user"
                    :size="GamPlayerCardSize.X_SMALL"
                    :auto-size="true"
                    :player="images[selectedImageIndex]?.user!"
                    @click="goToUser(images[selectedImageIndex]?.user)"
                  />
                </div>

                <button
                  v-if="canAssignPosition"
                  type="button"
                  class="position-assign"
                  :class="positionTone ? `is-${positionTone}` : undefined"
                  :aria-label="
                    shownPosition == null
                      ? localize('handshake.photo.assignPosition')
                      : localize('handshake.photo.changePosition')
                  "
                  @click="emits('position')"
                >
                  <span v-if="shownPosition != null">#{{ shownPosition }}</span>
                  <span v-else class="position-plus" />
                </button>
                <span v-else />

                <div class="viewer-end">
                  <gam-button v-if="canLike" v-bind="likedInfo" />
                  <div v-if="images[selectedImageIndex]?.createdAt" class="time-ago">
                    <gam-chip :left-icon="GamIconName.TIME" :label="dateLabel" />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="showArrows" class="viewer-arrow">
              <gam-button
                v-if="selectedImageIndex < images.length - 1"
                :is-icon-only="true"
                :left-icon="GamIconName.ARROW_RIGHT"
                :variant="GamButtonVariant.STEKLO"
                :size="GamButtonSize.ACTIONS"
                @click="selectImage(selectedImageIndex + 1)"
              />
            </div>
          </div>
        </div>

        <reporting-dialog
          v-if="showReportDialog"
          :id="images[selectedImageIndex].imageBlobHash"
          :type="ReportType.BLOB"
          :blob-source="blobSource"
          @close="showReportDialog = false"
        />
      </div>
    </transition>
  </dialog>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { AuthorDto } from '@/core/data/location/location.interface';
import { ReportType } from '@/core/data/report/report.type';
import { gambits, localize } from '@/core/gambits';
import { formatMonthLabel } from '@/core/helpers/date.helper';
import FormHelper from '@/core/helpers/form.helper';
import { isAndroid, isIOS, isMobile } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import ReportingDialog from '@/views/components/gam-dialog/dialog-contents/ReportingDialog.vue';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import GamPlayerCard from '@/views/components/gam-player/GamPlayerCard.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamImageFormat } from '@/views/composables/constants/components/gamImage.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import { GamPlayerCardSize } from '@/views/composables/constants/components/gamPlayer.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useImageLoader } from '@/views/composables/hooks/useImageLoader';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import type { GamMoreItem, GamMoreType } from '@/views/composables/models/components/GamMore';
import { useFileDialog, useSwipe } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = withDefaults(defineProps<GamExpandImageType>(), {
  format: GamImageFormat.VERTICAL,
  close: true,
  showAuthor: false,
  closeAfterDelete: true,
  isGallery: false,
  isURLBound: false,
  canLike: false,
  canReport: true,
  canChange: false,
  canAdd: false,
  addShortcut: false,
  canPinCover: false,
  pinnedCoverHash: null,
});

const emits = defineEmits([
  GamComponentsEmits.CLOSE,
  GamComponentsEmits.UPDATE,
  GamComponentsEmits.DELETE,
  GamComponentsEmits.UPDATE_VALUE,
  GamComponentsEmits.CHANGE,
  GamComponentsEmits.PIN_COVER,
  'position',
  'add',
]);

const appStore = useAppStore();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const router = useRouter();
const route = useRoute();

const { open: openChangePicker, onChange: onChangeFile } = useFileDialog({
  accept: '.jpg, .jpeg, .png',
  multiple: false,
});

const isChanging = ref(false);

const handleChange = () => {
  openChangePicker();
};

onChangeFile(async (files) => {
  if (files?.length) {
    isChanging.value = true;
    const base64 = await FormHelper.readFileAsBase64(files[0]);
    emits(GamComponentsEmits.CHANGE, `data:image/png;base64,${base64}`);
  }
});

const expandImage = ref();
const imageWrapperRef = ref<HTMLDivElement>();
const showReportDialog = ref(false);
const dialog = ref<HTMLDialogElement>();
const visible = ref<boolean>(false);

const getInitialIndex = (): number => {
  const { photo: id } = route.query;
  if (id) {
    const urlIndex = props.images.findIndex((img) => img.id === id || img.imageBlobHash === id);
    if (urlIndex >= 0) return urlIndex;
  }
  return props.selectedIndex ?? 0;
};
const currentIndex = ref(getInitialIndex());

const isPhone = computed(() => !!isIOS.value || !!isAndroid.value);

const SWIPE_THRESHOLD = 50;
useSwipe(imageWrapperRef, {
  passive: true,
  onSwipeEnd(_e, direction) {
    if (!isPhone.value || props.images.length <= 1) return;

    if (direction === 'left' && currentIndex.value < props.images.length - 1) {
      selectImage(currentIndex.value + 1);
    } else if (direction === 'right' && currentIndex.value > 0) {
      selectImage(currentIndex.value - 1);
    }
  },
  threshold: SWIPE_THRESHOLD,
});

const selectedImage = computed(() => props.images[currentIndex.value] ?? null);
const selectedImageIndex = computed(() => currentIndex.value);

const imageSource = computed(() => {
  if (!selectedImage.value?.imageBlobHash) return;

  return gambits.imageService.getImageSrc(selectedImage.value?.imageBlobHash, 1080);
});

const { width, height, isLoading, isLoaded } = useImageLoader(imageSource);

watch(imageSource, () => {
  isChanging.value = false;
});

type TImageSize = { width: number; height: number };
const lastSize = ref<TImageSize>({ width: 250, height: 250 });
const lastSource = ref<string | undefined>(imageSource.value);

watch(
  () => ({ width: width.value, height: height.value }),
  (newSize) => {
    if (newSize.width === -1) return;

    lastSize.value = newSize;
    lastSource.value = imageSource.value;
  },
  { immediate: true },
);

const dateLabel = computed(() => {
  const createdAt = selectedImage.value?.createdAt;
  if (!createdAt) return;

  return formatMonthLabel(createdAt, selectedImage.value?.timezone);
});

const likedInfo = computed((): GamButtonType => {
  const isLiked = !!props.images[selectedImageIndex.value]?.liked;
  const likeCount = props.images[selectedImageIndex.value]?.numberOfLikes || 0;
  return {
    variant: isLiked ? GamButtonVariant.PRIMARY : GamButtonVariant.STEKLO,
    size: GamButtonSize.ACTIONS,
    label: likeCount.toString(),
    leftIcon: isLiked ? GamIconName.HEART : GamIconName.HEART_EMPTY,
    isIconOnly: likeCount === 0,
    onClick: () => {
      emits(GamComponentsEmits.UPDATE, props.selectedIndex);
    },
  };
});

const MAX_VISIBLE_DOTS = 5;
const DOT_SIZE = 10;
const DOT_GAP = 6;
const DOT_STEP = DOT_SIZE + DOT_GAP;

const dotWindowStart = computed(() => {
  const total = props.images.length;
  if (total <= MAX_VISIBLE_DOTS) return 0;

  const center = Math.floor(MAX_VISIBLE_DOTS / 2);
  const start = selectedImageIndex.value - center;
  return Math.max(0, Math.min(total - MAX_VISIBLE_DOTS, start));
});

const dotTrackStyle = computed(() => {
  if (props.images.length <= MAX_VISIBLE_DOTS) return {};

  return {
    transform: `translateX(-${dotWindowStart.value * DOT_STEP}px)`,
  };
});

const selectImage = (index: number): void => {
  if (index < 0 || index >= props.images.length) return;

  isLoading.value = true;
  currentIndex.value = index;

  router.replace({
    path: route.path,
    query: { ...route.query, photo: props.images[index].id },
  });
};

const showModal = () => {
  dialog.value?.showModal();
  appStore.setImageExpanded(true);
  appStore.setDialogOpen(true);
  visible.value = true;
};

const closeModal = (): void => {
  if (showReportDialog.value) return;
  dialog.value?.close();
};

const stopEvent = (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
};

const hideDialog = (): void => {
  appStore.setImageExpanded(false);
  appStore.setDialogOpen(false);
  visible.value = false;
  emits(GamComponentsEmits.CLOSE);
};

const goToUser = (user: AuthorDto | null) => {
  if (!user) return;

  hideDialog();

  setTimeout(() => {
    router.push({ name: RouterNameType.USERNAME, params: { username: user.username! }, query: { back: 'history' } });
  });
};

const isCurrentImageOwner = computed(() => {
  const imageUser = selectedImage.value?.user;
  if (!imageUser?.id) return false;
  return imageUser.id === user.value?.id;
});

/** The Chess960 number on this photo. A meeting photo has none, and shows a plus. */
const shownPosition = computed(() => {
  const position = selectedImage.value?.positionIndex;
  return position == null ? null : position;
});

const canAssignPosition = computed(() => !!selectedImage.value?.canAssignPosition);

const positionTone = computed(() => selectedImage.value?.positionTone);

const showArrows = computed(() => !isPhone.value && props.images.length > 1);

const showTop = computed(
  () => props.images.length > 1 || props.canChange || props.addShortcut || !!getMoreItems.value.items?.length,
);

const showBottom = computed(
  () =>
    canAssignPosition.value ||
    props.canLike ||
    (!!props.showAuthor && !!props.images[selectedImageIndex.value]?.user) ||
    !!props.images[selectedImageIndex.value]?.createdAt,
);

const getMoreItems = computed((): GamMoreType => {
  const items: GamMoreItem[] = [];

  if (props.canAdd) {
    items.push({
      label: 'handshake.photo.add',
      onClick: () => {
        emits('add');
      },
    });
  }

  if (props.canPinCover && user.value?.isAdmin) {
    const isPinned = selectedImage.value?.imageBlobHash === props.pinnedCoverHash;
    items.push({
      label: isPinned ? 'app.more.unpin_cover' : 'app.more.pin_cover',
      onClick: () => {
        emits(GamComponentsEmits.PIN_COVER, isPinned ? null : selectedImage.value?.imageBlobHash);
      },
    });
  }

  if (props.canDelete || isCurrentImageOwner.value || user.value?.isAdmin) {
    items.push({
      label: 'app.more.delete',
      confirmation: 'app.more.imageDeleteConfirmation',
      onClick: () => {
        emits(GamComponentsEmits.DELETE, selectedImage.value?.imageBlobHash);
        nextTick(hideDialog);
      },
    });
  } else if (props.canReport) {
    items.push({
      label: 'app.more.report',
      onClick: () => {
        showReportDialog.value = true;
      },
    });
  }

  return {
    items,
    size: GamButtonSize.X_SMALL,
    position: GamMorePosition.LEFT_BELOW,
    icon: GamIconName.DOTS_V,
    variant: GamButtonVariant.TERTIARY,
    isIconWhite: true,
  };
});

defineExpose({
  show: showModal,
  close: closeModal,
  visible,
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.gam-expanded-image-dialog {
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: none;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  outline: 0;
  background: none;
  border: 0;
  margin: 0 auto;
  padding: 0;
  transition: visibility var(--transition-loader);
  color: var(--color-white-100);
  pointer-events: all;

  &::-webkit-scrollbar {
    display: none;
  }

  &::backdrop {
    display: none;
  }
}

.gam-expanded-frame {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--color-dark-70);
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  pointer-events: all !important;
  transform: translateZ(0);
  cursor: default;

  &.drawer {
    border-radius: 36px 0 0 36px;
  }
}

.gam-expand-image {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100dvh;
  max-width: calc(100% - 16vw);
  max-height: calc(100dvh - 16vh);

  &.is-mobile {
    max-width: calc(100% - 32px);
    max-height: calc(100dvh - 48px);
  }
}

.image-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border-radius: var(--radius-normal) !important;
  box-shadow: 0 0 var(--radius-normal) var(--color-dark-900);
  min-height: 0;
  flex: 0 1 auto;
  align-self: center;
  width: max-content;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  background: var(--color-dark-700) !important;

  .is-mobile & {
    width: 100%;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: calc(100dvh - 16vh - 140px);
    height: auto;
    width: auto;
    border-radius: inherit;
    opacity: 1;
    transition: opacity var(--transition-loader);
  }

  .is-mobile & img {
    width: 100%;
    max-height: calc(100dvh - 180px);
  }

  img.is-loading {
    opacity: 0;
    transition: none;
    width: 250px;
    height: 250px;
  }
}

.viewer {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  max-height: 100%;
  min-height: 0;
}

.viewer-arrow {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 34px;
}

.is-mobile .viewer,
.is-mobile .viewer-column {
  width: 100%;
}

.viewer-column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  max-width: 100%;
}

/* Left and right take equal room so the middle stays on the photo's centre. */
.viewer-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  flex: none;
  min-height: 34px;
  pointer-events: all;
}

.viewer-top {
  padding-bottom: 8px;
}

.viewer-bottom {
  padding-top: 8px;
}

.viewer-end {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.author-time {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  gap: 8px;

  :deep(.gam-player-card) {
    min-width: 0;
    max-width: 100%;
  }

  :deep(.username-wrapper) {
    min-width: 0;
  }
}

.position-assign {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  overflow: hidden;
  /* The boards icon: a 2×2 board in the light and dark square browns. */
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
  color: var(--color-white);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  cursor: pointer;

  /* One flat wash, so white type reads on the light squares as well as the dark. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
  }

  > * {
    position: relative;
    z-index: 1;
  }

  /* The collection's colours for this position, in place of the board. */
  &.is-unique,
  &.is-reported,
  &.is-repeat,
  &.is-unknown {
    background-image: none;

    &::before {
      content: none;
    }
  }

  &.is-unique {
    background-color: var(--color-pink);
  }

  &.is-reported {
    background-color: var(--color-reported);
  }

  &.is-repeat {
    background-color: var(--color-white);
    color: var(--color-dark-900);
  }

  &.is-unknown {
    background-color: var(--color-unknown);
  }

  @include press;
}

.position-plus {
  position: relative;
  width: 14px;
  height: 14px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: currentColor;
    border-radius: 1px;
  }

  &::before {
    top: 50%;
    left: 0;
    width: 100%;
    height: 1.5px;
    transform: translateY(-50%);
  }

  &::after {
    left: 50%;
    top: 0;
    width: 1.5px;
    height: 100%;
    transform: translateX(-50%);
  }
}

.image-selector {
  display: flex;
  height: 18px;
  align-items: center;
  justify-content: center;
}

.dot-viewport {
  display: flex;
  align-items: center;

  &.windowed {
    overflow: hidden;
    /* 5 dots × 10px + 4 gaps × 6px */
    max-width: calc(5 * 10px + 4 * 6px);
  }
}

.dot-track {
  display: flex;
  gap: 6px;
  transition: transform 0.25s ease;

  .dot-select {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--color-white-30);
    cursor: pointer;
    transition: opacity 0.25s ease;
  }

  .dot-select.selected {
    background-color: var(--color-white-100);
  }
}

.add-photo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-white);
  cursor: pointer;

  @include press;
}

.change-wrapper,
.more-wrapper {
  position: relative;
  justify-self: end;
  pointer-events: all;
  z-index: 1;
}

/* Same bare glyph as the three dots on a handshake row. */
.more-wrapper {
  :deep(.gam-button-container.is-icon) {
    width: 18px !important;
    height: 18px !important;
    min-height: 18px !important;
  }

  :deep(.svg-container) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
    min-width: 12px !important;
  }
}

.time-ago {
  flex: none;
}

.time-ago :deep(.gam-chip.is-default) {
  background: var(--color-white-5);
  backdrop-filter: blur(15px);
}
</style>
